// =====================================================================================
// INVRT Radar - web console - operator "Retire a delivery link" panel (Story W6B-R)
//
// A delivery link (the HIGH-INTENT binding for one organisation and campaign) can be left
// behind when the organisation's subscription is gone, for example a closed organisation.
// This panel lets the operator retire it: POST /admin/access/campaign-bindings/retire.
//
// The organisation list the operator console already reads includes closed organisations,
// so no extra toggle is needed; an organisation id may also be pasted (uuid-validated) for
// a row that is no longer listed. A confirm step precedes the call, and every answer is read
// fail-closed: only a 200 of the reviewed shape is believed, and anything else that is not a
// plain refusal reads as an unknown outcome rather than a success.
// =====================================================================================

import { useEffect, useRef, useState, type FormEvent } from "react";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Chip, type ChipSignal } from "@/components/ui/chip";
import { Input, Select } from "@/components/ui/input";
import { useApiClient } from "@/hooks/useApi";
import { isApiError } from "@/lib/api";
import { readCatalogueKind } from "@/pages/operator/catalogueReader";
import { useOperatorOrganisations } from "@/pages/operator/useOperatorOrganisations";
import { isUuid } from "./AccessSection";
import { readRetireResponse, RETIRE_PATH } from "./retireReader";

type Outcome =
  | { kind: "retired" }
  | { kind: "already" }
  | { kind: "forbidden" }
  | { kind: "not_sent" }
  | { kind: "active_subscription" }
  | { kind: "suspended_subscription" }
  | { kind: "refused_by_provider"; upstream: string | null }
  | { kind: "unknown" }
  | { kind: "rejected" };

const UNKNOWN_COPY = "Outcome unknown: refresh before retrying. The delivery link may or may not have been retired.";

const COPY: Record<Outcome["kind"], { label: string; signal: ChipSignal; text: string }> = {
  retired: { label: "Retired", signal: "buy", text: "Delivery link retired" },
  already: { label: "No change", signal: "ctx", text: "Nothing to retire: this link is already retired or never existed" },
  forbidden: { label: "Not allowed", signal: "risk", text: "You are not allowed to do this; sign in again as a platform administrator" },
  not_sent: { label: "Not retired", signal: "risk", text: "Nothing was sent; try again" },
  active_subscription: { label: "Not retired", signal: "risk", text: "An active subscription still holds this campaign for this organisation, so nothing was retired. Revoke the assignment first, then retire the link." },
  suspended_subscription: { label: "Not retired", signal: "risk", text: "A suspended subscription exists for this organisation and campaign and could be reinstated, so nothing was retired. Revoke the assignment first if it is to be abandoned." },
  refused_by_provider: { label: "Not retired", signal: "risk", text: "HIGH-INTENT refused the retire, so nothing was changed." },
  unknown: { label: "Unknown", signal: "pace", text: UNKNOWN_COPY },
  rejected: { label: "Not retired", signal: "risk", text: "The server refused this request, so nothing was changed. Check the organisation, the campaign and the reason." },
};

function classify(error: unknown): Outcome {
  if (!isApiError(error)) return { kind: "unknown" };
  // L-c: not allowed is not "unknown": the server refused before it acted.
  if (error.httpStatus === 401 || error.httpStatus === 403) return { kind: "forbidden" };
  // L-c: the subscription read and the audit row both precede the signed call, so these
  // two TOKENS prove nothing was sent. A generic 500 (internal_error or anything unlisted)
  // proves nothing, so it falls through to "outcome unknown", as does a 502/504/dropped
  // connection AFTER sending.
  if (error.token === "binding_retire_subscription_read_failed" || error.token === "binding_retire_audit_failed") return { kind: "not_sent" };
  if (error.httpStatus === 409) {
    if (error.token === "binding_retire_refused_active_subscription") return { kind: "active_subscription" };
    if (error.token === "binding_retire_refused_suspended_subscription") return { kind: "suspended_subscription" };
    if (error.token === "binding_retire_refused_by_high_intent") {
      const upstream = error.details?.["upstream_token"];
      return { kind: "refused_by_provider", upstream: typeof upstream === "string" && upstream.length > 0 && upstream.length <= 100 ? upstream : null };
    }
    return { kind: "unknown" };
  }
  // Only a 4xx (other than a request timeout) proves the server refused before acting.
  if (error.httpStatus >= 400 && error.httpStatus < 500 && error.httpStatus !== 408) return { kind: "rejected" };
  return { kind: "unknown" };
}

export function RetireBindingPanel() {
  const [open, setOpen] = useState(false);
  return (
    <section aria-label="Retire a delivery link" className="flex flex-col gap-4 rounded-card border border-line bg-surface p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex flex-col gap-1">
          <h2 className="text-lg font-semibold text-ink">Retire a delivery link</h2>
          <p className="max-w-2xl text-sm text-ink-soft">Close a HIGH-INTENT delivery link left behind for an organisation that no longer holds the campaign, such as a closed organisation.</p>
        </div>
        <Button type="button" variant="secondary" className="h-11" aria-expanded={open} onClick={() => setOpen((current) => !current)}>
          {open ? "Close retire panel" : "Retire a delivery link"}
        </Button>
      </div>
      {open ? <RetireForm /> : null}
    </section>
  );
}

function RetireForm() {
  const api = useApiClient();
  const organisations = useOperatorOrganisations();
  const campaigns = useQuery({ queryKey: ["operator", "retire", "campaigns"], queryFn: () => readCatalogueKind(api, "campaign"), staleTime: 30_000 });

  const [orgId, setOrgId] = useState("");
  const [pastedId, setPastedId] = useState("");
  const [campaignId, setCampaignId] = useState("");
  const [reason, setReason] = useState("");
  const [problem, setProblem] = useState<string | null>(null);
  const [pending, setPending] = useState<{ body: { radar_org_id: string; radar_campaign_id: string; reason: string }; orgLabel: string; campaignLabel: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [outcome, setOutcome] = useState<Outcome | null>(null);
  // M-2: focus goes into the dialog when it opens and back to the trigger when it closes by
  // the operator's own action (cancel, or the confirmed call finishing). A field edit also
  // clears the dialog but never steals focus from the field being edited.
  const dialogRef = useRef<HTMLDivElement>(null);
  const reviewRef = useRef<HTMLButtonElement>(null);
  const restoreFocus = useRef(false);
  useEffect(() => {
    if (pending) dialogRef.current?.focus();
    else if (restoreFocus.current) {
      restoreFocus.current = false;
      reviewRef.current?.focus();
    }
  }, [pending]);
  // M-2: any field change withdraws the pending confirmation, so a confirm can only ever
  // send the exact pair the dialog named.
  const edit = <T,>(set: (value: T) => void) => (value: T) => {
    setPending(null);
    set(value);
  };

  function review(event: FormEvent) {
    event.preventDefault();
    if (busy) return;
    setOutcome(null);
    setPending(null);
    const pasted = pastedId.trim();
    const chosenOrg = pasted !== "" ? pasted : orgId;
    if (pasted !== "" && !isUuid(pasted)) return setProblem("Enter a valid organisation id, or choose one from the list. Nothing was sent.");
    if (!isUuid(chosenOrg)) return setProblem("Choose an organisation. Nothing was sent.");
    if (!isUuid(campaignId)) return setProblem("Choose a campaign. Nothing was sent.");
    const trimmed = reason.trim();
    if (trimmed.length < 1 || trimmed.length > 500) return setProblem("Provide a reason between 1 and 500 characters. Nothing was sent.");
    setProblem(null);
    const orgName = organisations.data?.find((item) => item.id === chosenOrg)?.name;
    const campaignName = campaigns.data?.find((item) => item.id === campaignId)?.name ?? campaignId;
    setPending({
      body: { radar_org_id: chosenOrg, radar_campaign_id: campaignId, reason: trimmed },
      orgLabel: orgName ? `${orgName} (${chosenOrg})` : `${chosenOrg} (pasted id)`,
      campaignLabel: campaignName,
    });
  }

  async function confirm() {
    if (!pending || busy) return;
    const body = pending.body;
    setBusy(true);
    try {
      const response = await api.post<unknown>(RETIRE_PATH, body, { requiresOrg: false, includeOrg: false });
      const read = readRetireResponse(response);
      setOutcome(read === null ? { kind: "unknown" } : { kind: read.retired ? "retired" : "already" });
    } catch (error) {
      setOutcome(classify(error));
    } finally {
      restoreFocus.current = true;
      setPending(null);
      setBusy(false);
    }
  }

  const orgOptions = organisations.data ?? [];
  const campaignOptions = campaigns.data ?? [];
  const shown = outcome ? COPY[outcome.kind] : null;

  return (
    <form className="flex flex-col gap-4" onSubmit={review} noValidate>
      <Select label="Organisation" className="h-11" value={orgId} disabled={busy} onChange={(event) => edit(setOrgId)(event.target.value)}>
        <option value="">Choose an organisation</option>
        {orgOptions.map((item) => (
          <option key={item.id} value={item.id}>{item.status === "active" ? item.name : `${item.name} (${item.status})`}</option>
        ))}
      </Select>
      <Input label="Or paste an organisation id" className="h-11 font-mono" value={pastedId} disabled={busy} onChange={(event) => edit(setPastedId)(event.target.value)} hint="Use this for an organisation that is no longer listed. It takes priority over the list." />
      <Select label="Campaign" className="h-11" value={campaignId} disabled={busy} onChange={(event) => edit(setCampaignId)(event.target.value)}>
        <option value="">Choose a campaign</option>
        {campaignOptions.map((item) => (
          <option key={item.id} value={item.id}>{item.name}</option>
        ))}
      </Select>
      <Input label="Reason" className="h-11" value={reason} disabled={busy} maxLength={500} onChange={(event) => edit(setReason)(event.target.value)} />
      {problem ? <p role="alert" className="text-sm text-danger">{problem}</p> : null}
      {pending ? (
        <div ref={dialogRef} tabIndex={-1} role="alertdialog" aria-label="Confirm retire" className="flex flex-col gap-3 rounded-card border border-line p-3 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary">
          <p className="text-sm text-ink">This stops HIGH-INTENT delivery for this organisation and campaign. It cannot be undone from here.</p>
          <p className="text-sm text-ink"><span className="font-semibold">Organisation:</span> {pending.orgLabel}</p>
          <p className="text-sm text-ink"><span className="font-semibold">Campaign:</span> {pending.campaignLabel}</p>
          <div className="flex flex-wrap gap-2">
            <Button type="button" className="h-11" disabled={busy} onClick={() => void confirm()}>Confirm retire</Button>
            <Button type="button" variant="secondary" className="h-11" disabled={busy} onClick={() => { restoreFocus.current = true; setPending(null); }}>Cancel</Button>
          </div>
        </div>
      ) : (
        <div>
          <Button ref={reviewRef} type="submit" className="h-11" disabled={busy}>Review retire</Button>
        </div>
      )}
      {shown && outcome ? (
        <div role="status" className="flex flex-col gap-1 text-sm text-ink">
          <Chip signal={shown.signal}>{shown.label}</Chip>
          <span>{shown.text}</span>
          {outcome.kind === "refused_by_provider" && outcome.upstream ? <span className="font-mono text-xs text-ink-muted">{outcome.upstream}</span> : null}
        </div>
      ) : null}
    </form>
  );
}
